import { ReactNode, useRef, useState } from "react";
import {
  useFloating,
  shift,
  offset,
  flip,
  arrow,
  autoUpdate,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingArrow,
} from "@floating-ui/react";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    tooltipContainer:
      "absolute left-0 top-0 z-[1000] rounded bg-black px-10 py-5 text-center text-white opacity-0 md:opacity-100",
    floatingArrow: "fill-black",
  },
  variants: {
    maxWidth: {
      unset: {
        tooltipContainer: "w-max",
      },
      sm: {
        tooltipContainer: "w-[200px]",
      },
      md: {
        tooltipContainer: "w-[350px]",
      },
      lg: {
        tooltipContainer: "w-[500px]",
      },
    },
  },
});

interface IProps {
  direction?: "top" | "right" | "bottom" | "left";
  content: ReactNode;
  children: JSX.Element;
  maxWidth?: VariantProps<typeof styles>["maxWidth"];
}

const Tooltip = ({
  direction = "top",
  content,
  children,
  maxWidth = "unset",
}: IProps) => {
  const { tooltipContainer, floatingArrow } = styles({ maxWidth });
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(10),
      flip(),
      shift({
        padding: 10,
      }),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
    placement: direction,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  if (!content) return children;

  return (
    <div
      ref={refs.setReference}
      {...getReferenceProps()}
      className="relative inline-block">
      {children}
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={tooltipContainer()}
          {...getFloatingProps()}>
          <FloatingArrow
            ref={arrowRef}
            context={context}
            className={floatingArrow()}
          />
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
